import * as dashboardsService from "@hub/settings/src/modules/dashboards/services/dashboards.service"
import { generateTempId } from "@hub/settings/src/modules/dashboards/utils/tempId"

const buildFilterPayload = filter => ({
  id: filter.id,
  type: filter.type,
  label: filter.label,
  ...(filter.required && { required: filter.required }),
  ...(filter.default_value && { default_value: filter.default_value }),
  ...(filter.options?.length && { options: filter.options }),
  ...(filter.endpoint && { endpoint: filter.endpoint })
})

const buildChartPayload = chart => ({
  uuid: chart.uuid,
  type: chart.type,
  size: chart.size
})

const buildTabPayload = tab => ({
  id: tab.id,
  label: tab.label,
  indicators: tab.indicators,
  quick_filters: (tab.quickFilters || []).map(buildFilterPayload),
  charts: (tab.charts || []).map(buildChartPayload)
})

const buildDashboardPayload = currentDashboard => ({
  description: currentDashboard.description,
  roles: currentDashboard.roles,
  filters: currentDashboard.filters.map(buildFilterPayload),
  tabs: currentDashboard.tabs.filter(tab => !tab.draft).map(buildTabPayload)
})

export const fetchDashboard = async ({ commit, state }, uuid) => {
  commit("SET_LOADING", { key: "fetching", value: true })
  try {
    const data = await dashboardsService.fetchDashboard(uuid)
    if (data) {
      commit("SET_CURRENT_DASHBOARD", data)
      commit("SET_ACTIVE_TAB", state.currentDashboard.tabs[0]?.uuid || null)
    }
    return data
  } finally {
    commit("SET_LOADING", { key: "fetching", value: false })
  }
}

export const resetCurrentDashboard = ({ commit }) => {
  commit("RESET_CURRENT_DASHBOARD")
}

export const setActiveTab = ({ commit }, tabUuid) => {
  commit("SET_ACTIVE_TAB", tabUuid)
}

export const updateGeneralFields = ({ commit }, data) => {
  commit("PATCH_DASHBOARD_GENERAL", data)
}

export const updateRoles = ({ commit }, roles) => {
  commit("PATCH_DASHBOARD_GENERAL", { roles })
}

export const addTab = ({ commit }, data) => {
  const tab = { ...data, uuid: generateTempId() }
  commit("ADD_TAB", tab)
  commit("SET_ACTIVE_TAB", tab.uuid)
  return tab
}

export const editTab = ({ commit }, { tabUuid, data }) => {
  commit("UPDATE_TAB", { uuid: tabUuid, ...data })
}

export const removeTab = ({ commit }, tabUuid) => {
  commit("REMOVE_TAB", tabUuid)
}

export const addChart = ({ commit }, { tabUuid, data }) => {
  commit("ADD_CHART", { tabUuid, chart: data })
}

export const editChart = ({ commit }, { tabUuid, index, data }) => {
  commit("UPDATE_CHART", { tabUuid, index, chart: data })
}

export const removeChart = ({ commit }, { tabUuid, index }) => {
  commit("REMOVE_CHART", { tabUuid, index })
}

export const moveChart = ({ commit }, { tabUuid, indexA, indexB }) => {
  commit("MOVE_CHART", { tabUuid, indexA, indexB })
}

export const addFilter = ({ commit }, data) => {
  commit("ADD_FILTER", data)
}

export const removeFilter = ({ commit }, { scope, tabUuid, filterId }) => {
  commit("REMOVE_FILTER", { scope, tabUuid, filterId })
}

export const saveDashboard = async ({ commit, state }) => {
  commit("SET_LOADING", { key: "saving", value: true })
  try {
    const { uuid, title } = state.currentDashboard
    const payload = buildDashboardPayload(state.currentDashboard)

    return uuid
      ? await dashboardsService.updateDashboard(uuid, payload)
      : await dashboardsService.createDashboard({ title, ...payload })
  } finally {
    commit("SET_LOADING", { key: "saving", value: false })
  }
}
